import { Routes } from '@angular/router'
import { canActivateAuthGuardFn, canDeactivateDirtyFormGuard } from '@saniup-webapps/shared'
import { LoginComponent, NotFoundComponent } from '@saniup-webapps/shared/components'
import { InternalNavigationComponent } from './internal-navigation/internal-navigation.component'
import {
  anlageNurAufTestumgebungGuard,
  KRITERIUM_ANLAGE_GESPERRT_HINWEIS,
  VERTRAG_ANLAGE_GESPERRT_HINWEIS,
} from './intern/vertrag/anlage-nur-auf-testumgebung.guard'

export const routes: Routes = [
  {
    path: '',
    redirectTo: 'internal',
    pathMatch: 'full',
  },
  {
    path: 'landing',
    redirectTo: 'internal/landing',
    pathMatch: 'full',
  },
  {
    path: 'login',
    component: LoginComponent,
    title: 'Login | SaniUp',
  },
  {
    path: 'internal',
    component: InternalNavigationComponent,
    canActivate: [canActivateAuthGuardFn()],
    title: 'Internal | SaniUp',
    data: {
      title: 'Interne Verwaltung',
    },
    children: [
      {
        path: '',
        pathMatch: 'full',
        redirectTo: 'landing',
      },
      {
        path: 'landing',
        loadComponent: () => import('./landing-page/landing-page.component').then((m) => m.LandingPageComponent),
      },
      {
        path: 'profile',
        loadComponent: () => import('./auth/lazy-profile/lazy-profile.component').then((m) => m.LazyProfileComponent),
      },
      {
        path: 'leistungserbringer',
        loadComponent: () =>
          import('./intern/leistungserbringer-verwaltung/leistungserbringer-page/leistungserbringer-page.component').then(
            (m) => m.LeistungserbringerPageComponent,
          ),
      },
      {
        path: 'leistungserbringer/anlage',
        loadComponent: () =>
          import('./intern/leistungserbringer-verwaltung/leistungserbringer-verwaltung/leistungserbringer-verwaltung.component').then(
            (m) => m.LeistungserbringerVerwaltungComponent,
          ),
        canDeactivate: [canDeactivateDirtyFormGuard],
      },
      {
        path: 'leistungserbringer/:id',
        loadComponent: () =>
          import('./intern/leistungserbringer-verwaltung/leistungserbringer-detail/leistungserbringer-detail.component').then(
            (m) => m.LeistungserbringerDetailComponent,
          ),
        canDeactivate: [canDeactivateDirtyFormGuard],
      },
      {
        path: 'produktupload',
        loadComponent: () => import('./intern/produktupload/produktupload/produktupload.component').then((m) => m.ProduktuploadComponent),
      },
      {
        path: 'import',
        loadComponent: () => import('./intern/import/import-page/import-page.component').then((m) => m.ImportPageComponent),
        title: 'Import | SaniUp',
      },
      {
        path: 'fehlende-ek',
        loadComponent: () => import('./intern/fehlende-ek/fehlende-ek-page.component').then((m) => m.FehlendeEkPageComponent),
        title: 'Fehlende EK | SaniUp',
      },
      {
        path: 'vertrag',
        loadComponent: () => import('./intern/vertrag/vertrag-page/vertrag-page.component').then((m) => m.VertragPageComponent),
      },
      {
        path: 'vertrag',
        loadComponent: () =>
          import('./intern/vertrag/vertrag-anlage/vertrag-anlage-shell.component').then((m) => m.VertragAnlageShellComponent),
        children: [
          {
            path: 'anlage',
            canActivate: [anlageNurAufTestumgebungGuard('/internal/vertrag', VERTRAG_ANLAGE_GESPERRT_HINWEIS)],
            children: [
              {
                path: '',
                loadComponent: () =>
                  import('./intern/vertrag/vertrag-anlage/vertrag-anlage-choice.component').then((m) => m.VertragAnlageChoiceComponent),
              },
              {
                path: 'manuell',
                loadComponent: () =>
                  import('./intern/vertrag/vertrag-anlage/vertrag-anlage-form.component').then((m) => m.VertragAnlageFormComponent),
                canDeactivate: [canDeactivateDirtyFormGuard],
              },
              {
                path: 'ai',
                loadComponent: () =>
                  import('./intern/vertrag/vertrag-anlage/vertrag-anlage-ai.component').then((m) => m.VertragAnlageAiComponent),
              },
            ],
          },
          {
            path: ':id',

            loadComponent: () =>
              import('./intern/vertrag/vertrag-anzeige/vertrag-anzeige.component').then((m) => m.VertragAnzeigeComponent),
            canDeactivate: [canDeactivateDirtyFormGuard],
          },
        ],
      },
      {
        path: 'kriterien',
        loadComponent: () =>
          import('./intern/vertrag/kriterien/kriterien-page/kriterien-page.component').then((m) => m.KriterienPageComponent),
      },
      {
        path: 'kriterien/anlage',
        canActivate: [anlageNurAufTestumgebungGuard('/internal/kriterien', KRITERIUM_ANLAGE_GESPERRT_HINWEIS)],
        loadComponent: () =>
          import('./intern/vertrag/kriterien/kriterien-anlage/kriterien-anlage.component').then((m) => m.KriterienAnlageComponent),
      },
      {
        path: 'pflegebox/abrechnungsupload',
        loadComponent: () =>
          import('./intern/pflegebox/pflegebox-abrechnungs-csv-upload/pflegebox-abrechnungs-csv-upload.component').then(
            (m) => m.PflegeboxAbrechnungsCsvUploadComponent,
          ),
      },
      // EKV
      {
        path: 'ekv',
        loadComponent: () => import('./intern/ekv/ekv-page/ekv-page.component').then((m) => m.EkvPageComponent),
      },
      {
        path: 'ekv/:himiOrPflegeboxOrderId',
        loadComponent: () => import('./intern/ekv/ekv-detail/ekv-detail.component').then((m) => m.EkvDetailComponent),
      },
      {
        path: 'invoice',
        loadComponent: () => import('./intern/invoice/invoice-page/invoice-page.component').then((m) => m.InvoicePageComponent),
      },
      {
        path: 'finanzen',
        children: [
          {
            path: '',
            loadComponent: () =>
              import('./intern/finanzen/overview/finanzen-overview-page.component').then((m) => m.FinanzenOverviewPageComponent),
            title: 'Finanzen | SaniUp',
          },
          {
            path: 'prognose',
            loadComponent: () =>
              import('./intern/finanzen/prognose/finanzen-prognose-page.component').then((m) => m.FinanzenPrognosePageComponent),
            title: 'Prognose | Finanzen',
          },
          {
            path: 'sanitaetshaus',
            loadComponent: () =>
              import('./intern/finanzen/sanitaetshaus/finanzen-sanitaetshaus-ranking-page.component').then(
                (m) => m.FinanzenSanitaetshausRankingPageComponent,
              ),
            title: 'Sanitätshäuser | Finanzen',
          },
          {
            path: 'sanitaetshaus/:sanitaetshausId',
            loadComponent: () =>
              import('./intern/finanzen/sanitaetshaus/finanzen-sanitaetshaus-detail-page.component').then(
                (m) => m.FinanzenSanitaetshausDetailPageComponent,
              ),
            title: 'Sanitätshaus-Detail | Finanzen',
          },
          {
            path: 'sanitaetshaus/:sanitaetshausId/patient/:patientId',
            loadComponent: () =>
              import('./intern/finanzen/patient/finanzen-patient-detail-page.component').then((m) => m.FinanzenPatientDetailPageComponent),
            title: 'Patient-Detail | Finanzen',
          },
          {
            path: 'versorgung/:versorgungsId',
            loadComponent: () =>
              import('./intern/finanzen/versorgung/finanzen-versorgung-detail-page.component').then(
                (m) => m.FinanzenVersorgungDetailPageComponent,
              ),
            title: 'Versorgung-Detail | Finanzen',
          },
          {
            path: 'delivery/:deliveryId/products',
            loadComponent: () =>
              import('./intern/finanzen/delivery/finanzen-delivery-products-page.component').then(
                (m) => m.FinanzenDeliveryProductsPageComponent,
              ),
            title: 'Lieferung-Produkte | Finanzen',
          },
        ],
      },
      {
        path: 'lieferlandkarte',
        loadComponent: () => import('./intern/lieferlandkarte/lieferlandkarte-page.component').then((m) => m.LieferlandkartePageComponent),
        title: 'Lieferlandkarte | SaniUp',
      },
      {
        path: 'zustellquote',
        loadComponent: () => import('./intern/zustellquote/zustellquote-page.component').then((m) => m.ZustellQuotePageComponent),
        title: 'Fehlzustellungen | SaniUp',
      },
      // Absetzung
      {
        // Reiter „Absetzungen" und „Abrechnungsnachweise"; der Pfad bleibt fuer alte Links.
        path: 'absetzung/import',
        loadComponent: () => import('./intern/absetzung/einlesen-page/einlesen-page.component').then((m) => m.EinlesenPageComponent),
      },
      // Von den Haeusern hochgeladene Absetzungen und Abrechnungen (reine Ablage, kein KI-Import).
      // Backend-Repo: docs/claude/abrechnungs-unterlagen.md
      {
        path: 'unterlagen',
        loadComponent: () =>
          import('./intern/abrechnungs-unterlagen/abrechnungs-unterlagen-page.component').then((m) => m.AbrechnungsUnterlagenPageComponent),
        title: 'Absetzungen/Abrechnungen | SaniUp',
      },
      {
        path: 'unterlagen/:sanitaetshausId',
        loadComponent: () =>
          import('./intern/abrechnungs-unterlagen/abrechnungs-unterlagen-detail.component').then(
            (m) => m.AbrechnungsUnterlagenDetailComponent,
          ),
      },
      // Meldungen der Leistungserbringer zu ihren Vertragsbeitritten (Backend-Repo: docs/claude/vertragsbeitritte.md)
      {
        path: 'vertragsmeldungen',
        loadComponent: () =>
          import('./intern/vertragsmeldungen/vertragsmeldungen-page.component').then((m) => m.VertragsmeldungenPageComponent),
        title: 'Vertragsmeldungen | SaniUp',
      },
      // Operations
      {
        path: 'deadletter',
        loadComponent: () => import('./intern/deadletter/deadletter-page/deadletter-page.component').then((m) => m.DeadletterPageComponent),
      },
      {
        path: 'deadletter/:topic/:subscription',
        loadComponent: () => import('./intern/deadletter/deadletter-page/deadletter-page.component').then((m) => m.DeadletterPageComponent),
      },
      {
        path: 'deadletter/:topic/:subscription/:seq',
        loadComponent: () =>
          import('./intern/deadletter/deadletter-detail/deadletter-detail.component').then((m) => m.DeadletterDetailComponent),
      },
      {
        path: '**',
        component: NotFoundComponent,
      },
    ],
  },
  {
    path: '**',
    component: NotFoundComponent,
  },
]
